import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-lazy-hydration attribute.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: false, url });
  
  const signinLink = document.querySelectorAll('.account-actions__sign-in-link');
  if (signinLink.length) {
    signinLink.forEach(link => {
      const linkHref = link.getAttribute('href');
      if (!linkHref) return;

      // Split the href at '?return_to=' to get the base and the return_to value
      const [base, params] = linkHref.split('?return_to=');
      if (!params) return;

      // Split params at '&' to separate return_to from other params (like locale, ui_hint)
      const [returnToEncoded, ...otherParamsArr] = params.split('&');
      if (!returnToEncoded) return;
      let decodedReturnTo = decodeURIComponent(returnToEncoded);

      // Remove section_id from the return_to query string
      const [path, queryString] = decodedReturnTo.split('?');
      let cleanQuery = '';
      if (queryString) {
        cleanQuery = queryString
          .split('&')
          .filter(param => !param.startsWith('section_id='))
          .join('&');
      }
      const cleanedReturnTo = cleanQuery ? `${path}?${cleanQuery}` : path;
      const safeReturnTo = cleanedReturnTo || '/';
      // Rebuild the href
      const newHref = `${base}?return_to=${encodeURIComponent(safeReturnTo)}${otherParamsArr.length ? '&' + otherParamsArr.join('&') : ''}`;
      link.setAttribute('href', newHref);
    });
  }
  section.dataset.hydrated = 'true';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
